import {createItem, insertItem, setProp, updateItem, mapForEditing, isChild, mapError} from './helpers'
import {setFetching} from '../../../helpers'

export default {

  // -----------------------------
  // root level mutations
  // -----------------------------

  setFetching,

  setHasApiErrors (state, value) {
    state.hasApiErrors = value
  },

  setSaving (state, value) {
    state.saving = value
  },

  showMoveModal (state, id) {
    state.modals.move.visible = true
    state.modals.move.id = id
  },

  hideMoveModal (state) {
    state.modals.move.visible = false
    state.modals.move.id = null
  },

  showMediaLibraryModal (state) {
    state.modals.mediaLibrary.visible = true
  },

  hideMediaLibraryModal (state) {
    state.modals.mediaLibrary.visible = false
  },

  showImageCropModal (state) {
    state.modals.imageCrop.visible = true
  },

  hideImageCropModal (state) {
    state.modals.imageCrop.visible = false
  },

  updateRowIDs (state, {items, topicID}) {
    for (let i=0; i<items.length; i++) {
      const oldID = state.editing.ids[i]
      const newID = items[i].id
      if (oldID !== newID) {
        state.editing.ids[i] = newID

        // copy the old item and update id
        state.editing.rows = {
          ...state.editing.rows,
          [newID]: {
            ...state.editing.rows[oldID],
            id: newID
          }
        }

        // delete the old item
        delete state.editing.rows[oldID]

      }
    }
  },

  setIndex (state, {items, topicID}) {
    // TODO only remove rows and children for this topic

    // clear rows being edited
    state.index = {
      rows: {},
      children: {},
      ids: []
    }

    // add new items
    items.forEach((item) => {
      // normalize rows
      if (item.children) {
        item.children.forEach((child) => {
          state.index.children = {
            ...state.index.children,
            [child.id]: child
          }
        })

        // maintain original order of children
        item.children = item.children.map((child) => child.id)
      }

      state.index.rows = {
        ...state.index.rows,
        [item.id]: item
      }
    })

    // maintain original order of rows
    state.index.ids = Object.keys(state.index.rows)
  },

  setEditing (state, topicID) {
    // get the items for this topic
    let items = state.index.ids.map((id) => {
      return state.index.rows[id]
    }).filter((item) => {
      return item.topic && item.topic.id === topicID
    })

    // shed observers
    items = JSON.parse(JSON.stringify(items))

    // clear rows being edited
    state.editing = {
      rows: {},
      children: {},
      ids: []
    }

    items.forEach((item) => {
      // normalize rows
      if (item.children) {
        item.children.forEach((childID) => {
          const child = state.index.children[childID]
          state.editing.children = {
            ...state.editing.children,
            [childID]: mapForEditing({item: child, parentID: item.id})
          }
        })
      }

      state.editing.rows = {
        ...state.editing.rows,
        [item.id]: mapForEditing({item})
      }
    })

    state.editing.ids = Object.keys(state.editing.rows)
  },

  // -----------------------------
  // individual item mutations
  // -----------------------------

  setProps (state, {id, props} = {}) {
    updateItem({state, id, props})
  },

  setFormSaving (state, {id, value} = {}) {
    setProp({state, id, prop: 'form.saving', value})
  },

  setFormSuccess (state, {id, value} = {}) {
    setProp({state, id, prop: 'form.success', value})
  },

  setFormField (state, {id, field, value} = {}) {
    setProp({state, id, prop: `form.fields.${field}`, value})
  },

  resetFormFields (state, id) {
    const fields = state.editing.children[id].form.fields
    for (const field in fields) {
      setProp({state, id, prop: `form.fields.${field}`, value: null})
    }
  },

  resetFormErrors (state, id) {
    const apiErrors = state.editing.children[id].form.apiErrors
    for (const error in apiErrors) {
      setProp({state, id, prop: `form.apiErrors.${error}`, value: []})
    }
  },

  setFormErrors (state, {id, errors} = {}) {
    for (const error in errors) {
      setProp({state, id, prop: `form.apiErrors.${error}`, value: errors[error]})
    }
  },

  addItem (state, {nextID = null, parentID = null, type} = {}) {
    const item = createItem({state, type, parentID})
    insertItem({state, nextID, parentID, item})
  },

  removeItem (state, id) {
    if (isChild({state, id})) {
      const parentID = state.editing.children[id].parentID
      delete state.editing.children[id]
      const idx = state.editing.rows[parentID].children.indexOf(id)
      state.editing.rows[parentID].children.splice(idx, 1)
    } else {
      delete state.editing.rows[id]
      const idx = state.editing.ids.indexOf(id)
      state.editing.ids.splice(idx, 1)
    }
  },

  moveByOne (state, {id, direction}) {
    if (isChild({state, id})) {
      const parentID = state.editing.children[id].parentID
      const idx = state.editing.rows[parentID].children.indexOf(id)
      state.editing.rows[parentID].children.splice(idx, 1)
      state.editing.rows[parentID].children.splice(idx + direction, 0, id)
    } else {
      const idx = state.editing.ids.indexOf(id)
      state.editing.ids.splice(idx, 1)
      state.editing.ids.splice(idx + direction, 0, id)
    }
  },

  moveItem (state, {id, position, selectedID, appendToRow}) {
    // remove the id being moved
    if (isChild({state, id})) {
      const parentID = state.editing.children[id].parentID
      const idx = state.editing.rows[parentID].children.indexOf(id)
      state.editing.rows[parentID].children.splice(idx, 1)
    } else {
      const idx = state.editing.ids.indexOf(id)
      state.editing.ids.splice(idx, 1)
    }

    // appending to an empty row
    if (appendToRow) {
      state.editing.rows[selectedID].children.push(id)

    // inserting into a row
    } else if (isChild({state, id: selectedID})) {
      const parentID = state.editing.children[selectedID].parentID
      let idx = state.editing.rows[parentID].children.indexOf(selectedID)
      if (position === 'after') {
        idx++
      }
      state.editing.rows[parentID].children.splice(idx, 0, id)

    // inserting at root level
    } else {
      let idx = state.editing.ids.indexOf(selectedID)
      if (position === 'after') {
        idx++
      }
      state.editing.ids.splice(idx, 0, id)
    }
  },

  toggleComplete (state, item) {
    const update = {is_complete: !item.is_complete}

    state.index.rows[item.id] = {
      ...state.index.rows[item.id],
      ...update
    }
  },

  setApiErrors (state, errors) {
    const regFindPosition = /items\.(\d+)/
    for (const key in errors) {
      if (regFindPosition.test(key)) {
        const itemErrors = mapError(errors[key])
        const idx = parseInt(regFindPosition.exec(key)[1])
        const id = state.apiRequestOrder[idx]
        if (state.editing.rows[id]) {
          state.editing.rows[id].apiErrors = itemErrors
        } else if (state.editing.children[id]) {
          state.editing.children[id].apiErrors = itemErrors
        }
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/items/mutations.js